// Story 3.7 — effective animation mode for the reveal ceremony.
//
// Combines two signals into a single boolean-ish resolution:
//   1. User-level preference (users.animation_preference — auto|reduced|full)
//   2. OS-level prefers-reduced-motion: reduce media query
//
// Resolution rules per UX-DR31 + epic AC L1400:
//   user pref "full"    → "full"            (override OS)
//   user pref "reduced" → "reduced-motion"  (force, ignore OS)
//   user pref "auto"    → matchMedia        (mirror OS — default for every user)
//
// Loading/error fallback: when `useAccountMe()` is loading or has
// errored (e.g. anonymous visitor on the public prototype route), the
// hook treats the preference as `"auto"` so the OS preference still
// drives the ceremony. This is the safest default — a user with the
// OS preference set who doesn't have an account yet still gets the
// reduced-motion variant.
//
// Consumers (Story 3.9 welcome page; Story 4.x vault grid) compose:
//   const mode = useEffectiveMotionMode();
//   <RevealCeremony mode={mode} ... />
// The orchestrator (Story 3.6) itself stays unaware of preferences —
// it just accepts the resolved literal.

import { useAccountMe } from "../admin/useAccountMe";
import { useMatchMedia } from "../../lib/useMatchMedia";

export type EffectiveMotionMode = "full" | "reduced-motion";

export function useEffectiveMotionMode(): EffectiveMotionMode {
  const accountMe = useAccountMe();
  const prefersReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)");

  const userPreference = accountMe.data?.data.animation_preference ?? "auto";

  if (userPreference === "full") return "full";
  if (userPreference === "reduced") return "reduced-motion";
  // userPreference === "auto" — mirror OS.
  return prefersReducedMotion ? "reduced-motion" : "full";
}
